import { useCallback, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, MenuItem } from "@mui/material"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"
import { useFormikContext } from "formik"
import { ApiQueryTable } from "../../../../../../../../core/api/components/ApiQueryTable"
import { StandardMainPanel } from "../../../../../../../../shared/components/forms/StandardMainPanel"
import { ClosingTime, ClosingTimeType, CreateRoomResourceForm } from "../../../resourcesTypes"
import { StandardForm } from "../../../../../../../../shared/components/forms/StandardForm"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { StandardButton } from "../../../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { FormikDropdown, FormikTimePicker } from "../../../../../../../../shared/components/forms/Formik"
import { AddClosingTimeDialog } from "./AddClosingTimeDialog"
import { useColumnFactory } from "../../../../../../../../core/api/useTableColumns"
import { StandardSidePanel } from "../../../../../../../../shared/components/forms/StandardSidePanel"

const useStyles = makeStyles()((theme) => ({
    tablePaper: {
        boxShadow: "none",
    },
    tableHeadCell: {
        border: "none",
        color: theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: theme.palette.tailwind.slate[50],
    },
    timeInput: {
        width: "100%",
    },
}))

export function AvailabilityContent() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { setFieldValue, values } = useFormikContext<CreateRoomResourceForm>()
    const [shodAddDialog, setShodAddDialog] = useState(false)

    const periodsClosingTimes = useMemo(
        () => values.closingTimes.filter((i) => i.type === ClosingTimeType.PERIOD),
        [values.closingTimes],
    )

    const columnFactory = useColumnFactory<ClosingTime>()

    const columns = useMemo<MRT_ColumnDef<ClosingTime>[]>(
        () => [
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("tab-availability-table-start-header"),
                    accessorKey: "dateTimeInterval.from",
                },
                (row) => row.dateTimeInterval?.from,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("tab-availability-table-end-header"),
                    accessorKey: "dateTimeInterval.to",
                },
                (row) => row.dateTimeInterval?.to,
            ),
            columnFactory.shortNameColumn({
                header: l10n.getString("tab-availability-table-reason-header"),
                accessorKey: "reason",
            }),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ closeMenu }: { row: MRT_Row<ClosingTime>; closeMenu: () => void }) => [
            <MenuItem
                key="edit"
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-edit-action">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key="delete"
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-delete-action">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [setFieldValue],
    )

    return (
        <StandardForm>
            <LocalizedStrict id="tab-availability-side-daily-booking-times-title" attrs={{ title: true }}>
                <StandardSidePanel title="Accept Booking Time:/Block Booking Time:">
                    <LocalizedStrict id="tab-availability-modal-booking-time-dropdown-label" attrs={{ label: true }}>
                        <FormikDropdown name="bookingTimeType" label="Booking time">
                            <MenuItem value="acceptBookingTime">
                                <LocalizedStrict id="tab-availability-modal-booking-time-dropdown-accept">
                                    <span>Accept Booking Time:</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value="blockBookingTime">
                                <LocalizedStrict id="tab-availability-modal-booking-time-dropdown-block">
                                    <span>Block Booking Time:</span>
                                </LocalizedStrict>
                            </MenuItem>
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict id="tab-availability-side-start-time-label" attrs={{ label: true }}>
                        <FormikTimePicker
                            className={classes.timeInput}
                            label="Start time"
                            name="bookingTime.periodInTime.from"
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="tab-availability-side-end-time-label" attrs={{ label: true }}>
                        <FormikTimePicker
                            className={classes.timeInput}
                            label="End time"
                            name="bookingTime.periodInTime.to"
                        />
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>
            <StandardMainPanel variant="collapsible" title={l10n.getString("tab-availability-closed-periods-title")}>
                <ApiQueryTable
                    isLoading={false}
                    isFetching={false}
                    isError={false}
                    data={periodsClosingTimes}
                    columns={columns}
                    enableClickToCopy={false}
                    enableColumnOrdering
                    enableStickyHeader
                    enableDensityToggle={false}
                    initialState={{ density: "spacious" }}
                    enableHiding={false}
                    enableRowActions
                    enableBottomToolbar={false}
                    muiTablePaperProps={{ className: classes.tablePaper }}
                    muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                    muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                    positionActionsColumn="last"
                    enableColumnDragging={false}
                    renderRowActionMenuItems={renderRowActionMenuItems}
                    renderTopToolbar={<></>}
                />
                <Box>
                    <LocalizedStrict id="tab-availability-add-closed-period-action">
                        <StandardButton
                            onClick={() => setShodAddDialog(true)}
                            color="blue"
                            fullWidth={false}
                            variant="contained"
                        >
                            Add unavailable period
                        </StandardButton>
                    </LocalizedStrict>
                    {shodAddDialog && (
                        <AddClosingTimeDialog
                            open={shodAddDialog}
                            onClose={() => setShodAddDialog(false)}
                            onAdd={(newItem) => {
                                setFieldValue("closingTimes", [
                                    ...values.closingTimes,
                                    {
                                        type: ClosingTimeType.PERIOD,
                                        dateTimeInterval: {
                                            from: newItem.start,
                                            to: newItem.end,
                                        },
                                        reason: newItem.reason,
                                    },
                                ])
                                setShodAddDialog(false)
                            }}
                        />
                    )}
                </Box>
            </StandardMainPanel>
        </StandardForm>
    )
}
